import { formatDecimal, tt, ZONE_COLORS, type DeliveryZone, type PriceTier } from '@agos/shared'

/**
 * Editing the delivery zones.
 *
 * The zones are saved as **one array, with `PUT /delivery-zones` replacing the
 * whole collection.** That is the shape legacy uses and the API expects, and it
 * has a consequence worth stating where somebody will read it: a save sends
 * every zone, so a save built from partial state deletes whatever it left out.
 * The page therefore edits one local array and writes it whole.
 *
 * (I proved that the hard way while porting this: an empty `PUT` as an
 * existence probe removed all 31 zones from the local database.)
 *
 * Ported from legacy `pages/delivery-zones.tsx`.
 */

const DEFAULT_RADIUS_METRES = 3000
const DEFAULT_MIN_ORDER = 8
const DEFAULT_FEE = 2
/** What a new tier's lower bound steps by when the previous one is open-ended. */
const TIER_STEP = 15

/** Legacy's default name for a zone nobody has named yet. */
const NEW_ZONE_NAME = tt('Nová oblasť')

export function newTier(orderFrom = 0, makeId: () => string): PriceTier {
  return { id: makeId(), orderFrom, orderTo: null, deliveryFee: DEFAULT_FEE }
}

export function newZone(colorIndex: number, makeId: () => string): DeliveryZone {
  return {
    id: makeId(),
    name: NEW_ZONE_NAME,
    color: ZONE_COLORS[colorIndex % ZONE_COLORS.length] ?? ZONE_COLORS[0] ?? '#3b82f6',
    enabled: true,
    type: 'circle',
    center: undefined,
    radius: DEFAULT_RADIUS_METRES,
    polygon: [],
    minOrder: DEFAULT_MIN_ORDER,
    tiers: [newTier(0, makeId)],
  }
}

/**
 * Where a newly added tier starts.
 *
 * The previous tier's upper bound, so the bands meet with no gap. When the
 * previous one is open-ended there is no upper bound to meet, so it steps by a
 * round amount and leaves the manager to correct it — better than starting at
 * zero, which would overlap everything already entered.
 */
export function nextTierLowerBound(tiers: PriceTier[]): number {
  const last = tiers[tiers.length - 1]
  if (!last) return 0
  if (last.orderTo !== null && last.orderTo !== undefined) return Number(last.orderTo)
  return Number(last.orderFrom) + TIER_STEP
}

export function addTier(zone: DeliveryZone, makeId: () => string): DeliveryZone {
  return { ...zone, tiers: [...zone.tiers, newTier(nextTierLowerBound(zone.tiers), makeId)] }
}

export function updateTier(
  zone: DeliveryZone,
  tierId: string,
  changes: Partial<PriceTier>,
): DeliveryZone {
  return {
    ...zone,
    tiers: zone.tiers.map((tier) => (tier.id === tierId ? { ...tier, ...changes } : tier)),
  }
}

export function removeTier(zone: DeliveryZone, tierId: string): DeliveryZone {
  return { ...zone, tiers: zone.tiers.filter((tier) => tier.id !== tierId) }
}

/**
 * A zone that cannot be matched against an address.
 *
 * A circle with no centre and a polygon with fewer than three corners both
 * silently never match, so `findZoneForPoint` skips them — which means an
 * address that should have been in one is quoted as undeliverable instead. The
 * editor says so rather than letting it save looking finished.
 */
export function isZoneDrawn(zone: DeliveryZone): boolean {
  if (zone.type === 'circle') return Boolean(zone.center) && (zone.radius ?? 0) > 0
  return (zone.polygon?.length ?? 0) >= 3
}

/** What the list shows under a zone's name. */
export function describeZone(zone: DeliveryZone): string {
  if (zone.type === 'circle') {
    if (!zone.center) return tt('nenakreslený')
    return `r=${formatDecimal((zone.radius ?? 0) / 1000, 1)} km`
  }
  return `${zone.polygon?.length ?? 0}`
}

/** Where the map opens: the first zone with a centre, else Žilina. */
export const MAP_FALLBACK_CENTRE = { lat: 49.2239, lng: 18.7394 }

export function mapCentre(zones: DeliveryZone[]): { lat: number; lng: number } {
  const withCentre = zones.find((zone) => zone.center)
  if (withCentre?.center) return withCentre.center
  const withPolygon = zones.find((zone) => (zone.polygon?.length ?? 0) > 0)
  const first = withPolygon?.polygon?.[0]
  return first ?? MAP_FALLBACK_CENTRE
}

/** `#3b82f6` → `rgba(59,130,246,0.2)`, for a zone's fill. */
export function hexToRgba(hex: string, alpha: number): string {
  const digits = hex.replace('#', '')
  if (digits.length !== 6) return `rgba(0,0,0,${alpha})`
  const red = Number.parseInt(digits.slice(0, 2), 16)
  const green = Number.parseInt(digits.slice(2, 4), 16)
  const blue = Number.parseInt(digits.slice(4, 6), 16)
  if (!Number.isFinite(red) || !Number.isFinite(green) || !Number.isFinite(blue)) {
    return `rgba(0,0,0,${alpha})`
  }
  return `rgba(${red},${green},${blue},${alpha})`
}
